import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { AddressProps, AddressKey } from "pages/home/types";
import countries from "pages/utils/countries.json";
import useGetProvince from "hooks/useGetProvince";
import { Loading } from "components/index";

const stateProvince = ["AU", "IT", "ZA"];
const propertyIndexMapping: { [key: string]: number } = {
  IT: 2,
  ZA: 1,
  AU: 0,
};

const Address = ({
  userInfoData,
  onInputChange,
  validationError,
}: AddressProps) => {
  const address = userInfoData?.kyc?.address;
  const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = React.useState<
    string | undefined
  >(address?.addr_country_id);

  const url = selectedCountry
    ? `https://portal-api.tradenation.com/signup/TD365AU/step1${selectedCountry}/`
    : null;
  const { data: province, loading, error: provinceError } = useGetProvince(url);
  const addressFields: AddressKey[] = [
    "addr_street",
    "addr_line_2",
    "addr_zip",
    "addr_city",
  ];

  const handleInputChangeTextField =
    (key: AddressKey) => (event: React.ChangeEvent<HTMLInputElement>) => {
      onInputChange("address", key, event.target.value);
    };

  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    setSelectedCountry(event.target.value);
    onInputChange("address", "addr_country_id", event.target.value);
  };

  const handleStateChange = (event: SelectChangeEvent<string>) => {
    const stateKey = () => {
      switch (selectedCountry) {
        case "AU":
          return "au_state";
        case "IT":
          return "it_province";
        case "ZA":
          return "za_province";
        default:
          return "addr_state";
      }
    };

    const keysToReset = [
      "au_state",
      "it_province",
      "za_province",
      "addr_state",
    ];

    keysToReset.forEach((key) => {
      onInputChange("address", key, null);
    });

    // Set the value for the selected province/state field
    onInputChange("address", stateKey(), event.target.value, selectedCountry);
  };

  const getProvinceValueKey = (country = "") => {
    switch (country) {
      case "AU":
        return "au_state";
      case "IT":
        return "it_province";
      case "ZA":
        return "za_province";
      default:
        return "addr_state";
    }
  };

  React.useEffect(() => {
    if (validationError) {
      setIsExpanded(true);
    }
  }, [validationError]);

  const handleAccordionChange = (
    event: React.SyntheticEvent,
    expanded: boolean
  ) => {
    setIsExpanded(expanded);
  };
  const propertyIndex = propertyIndexMapping[selectedCountry ?? "AU"] ?? 0;

  const sortedCountries = countries.sort((a, b) => {
    return a.display_name.localeCompare(b.display_name);
  });
  const addressString = Object.values(address)
    .filter((value) => value) // Remove empty strings, null, or undefined
    .join(" ");

  return (
    <Accordion
      expanded={isExpanded}
      onChange={handleAccordionChange}
      elevation={0}
    >
      {loading && <Loading />}
      <AccordionSummary
        expandIcon={<ArrowDownwardIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography>Address</Typography>

          <Typography variant="caption" sx={{ color: "gray", lineHeight: 1.8 }}>
            {addressString || "Incomplete"}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          sx={{
            "& .MuiTextField-root": { m: 1 },
            display: "flex",
            flexDirection: "column",
          }}
        >
          {address &&
            addressFields.map((key) => (
              <TextField
                key={key}
                id={`address-${key}`}
                label={key.replace("addr_", "").replace("_", " ")}
                value={address[key] ?? ""}
                variant="outlined"
                size="small"
                onChange={handleInputChangeTextField(key)}
              />
            ))}
          <FormControl sx={{ m: 1, minWidth: 200 }}>
            <InputLabel id="country-select-label">Country</InputLabel>
            <Select
              id="country-select"
              labelId="country-select-label"
              value={selectedCountry ?? ""}
              onChange={handleCountryChange}
              label="Country"
            >
              {sortedCountries.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.display_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {province &&
            stateProvince.includes(selectedCountry ?? "") &&
            province.properties &&
            province.properties.length > 0 && (
              <FormControl sx={{ m: 1, minWidth: 200 }}>
                <InputLabel id="state-select-label">
                  {selectedCountry === "ZA" || selectedCountry === "IT"
                    ? "Province"
                    : "State"}
                </InputLabel>
                <Select
                  id="state-select"
                  labelId="state-select-label"
                  value={address?.[getProvinceValueKey(selectedCountry)] ?? ""}
                  onChange={handleStateChange}
                  label={
                    selectedCountry === "ZA" || selectedCountry === "IT"
                      ? "Province"
                      : "State"
                  }
                >
                  {province?.properties?.[propertyIndex]?.options?.map(
                    (item: any) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            )}
          {validationError && (
            <Typography color="error" sx={{ mt: 1 }}>
              {validationError}
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default Address;
